import React, { useContext } from 'react';
import * as Yup from 'yup';
import { FormikValues } from 'formik';
import {
  AppContext,
  AppState,
  initialState,
} from '../context/AppContext/context';

type Options<TValues extends FormikValues = any> = Omit<
  Partial<AppState['awaitedFormModal']>,
  'status' | 'resolve' | 'form'
> & {
  form: Omit<
    NonNullable<AppState<TValues>['awaitedFormModal']['form']>,
    'onSubmit'
  >;
};

const initialFormState = initialState.awaitedFormModal;

function useAwaitedForm() {
  const { dispatch } = useContext(AppContext);

  const update = React.useCallback(
    (payload: typeof initialFormState) => {
      dispatch({
        type: 'SET_AWAITED_FORM_MODAL',
        payload,
      });
    },
    [dispatch],
  );

  const onAwaitForm = React.useCallback(
    async <TValues extends FormikValues = any>(options: Options<TValues>) =>
      new Promise<TValues | null>(resolve => {
        if (!options.form?.inputRows?.length) {
          resolve(null);
          return;
        }

        const onResolve = (response: TValues | null) => {
          const validationSchema: Yup.ObjectSchema<TValues> =
            options.form?.validationSchema;
          const isValid =
            !validationSchema || validationSchema.isValidSync(response);
          resolve(isValid ? response : null);
          update({
            ...initialFormState,
            status: isValid ? 'resolved' : 'rejected',
          });
        };

        update({
          ...initialFormState,
          ...options,
          form: {
            ...options.form,
            onSubmit: onResolve,
          },
          status: 'awaiting',
          resolve: onResolve,
        });
      }),
    [update],
  );

  return onAwaitForm;
}

export default useAwaitedForm;
